/**
 * Registry for named action handlers referenced by `field.action.handler` in manifests.
 * Apps register handlers at startup; the ConfigEngine dispatches to them when the user
 * clicks a field's trailing action button.
 *
 * Intentional app-lifetime singleton (module-level Map, not Zustand):
 * Handlers are registered once at module load and never change per-session.
 * Not cleared on logout/team switch — handlers are stateless functions, not user data.
 */
import type { ApolloClient } from '@apollo/client';
import type { UseFormReturn } from 'react-hook-form';
import { toast } from 'sonner';
import type { AppMeta, ConfigField } from '@scio3/shared-types';

export interface ActionHandlerContext {
  /** Current form values (flattened — nested keys are objects) */
  values: Record<string, unknown>;
  /** Form instance — use form.setValue / form.getValues for direct manipulation */
  form: UseFormReturn<Record<string, unknown>>;
  /** AppMeta for apiUrl, backendUrl, etc. May be null in playground. */
  appMeta: AppMeta | null;
  /** i18n resolver */
  t: (key: string) => string;
  /** The field that triggered the action */
  field: ConfigField;
  /** Apollo client — for handlers that need to run GraphQL queries/mutations. */
  apolloClient: ApolloClient;
  /** True when the handler is invoked from the pre-save pipeline (action.runOnSave),
   *  false (default) when invoked by the manual button click. Handlers should suppress
   *  success-toasts in save mode and return `{ ok: false, reason }` to abort save. */
  isSaveCheck?: boolean;
  /** How the action was triggered. `'manual'` = button click (default); `'blur'` / `'change'` =
   *  the `runOnBlur` / `runOnChange` auto-triggers; `'mount'` = the `runOnMount` fire-once-on-open
   *  auto-trigger (edit-mode rehydration); `'save'` = the pre-save pipeline (also sets `isSaveCheck`).
   *  Handlers that only want to toast on an explicit user action should gate on `trigger === 'manual'`
   *  (the auto/save triggers surface their result elsewhere, e.g. a flag). */
  trigger?: 'manual' | 'blur' | 'change' | 'mount' | 'save';
}

/** Return value from an action handler. `void` (default) means "ok, no opinion".
 *  When `{ ok: false }`, the pre-save pipeline aborts save and toasts `reason`. */
export type ActionHandlerResult = void | { ok: boolean; reason?: string };

export type ActionHandler = (
  ctx: ActionHandlerContext,
) => ActionHandlerResult | Promise<ActionHandlerResult>;

const handlers = new Map<string, ActionHandler>();

/** Register a named action handler. Overwrites any existing handler with the same name. */
export function registerActionHandler(name: string, fn: ActionHandler): void {
  handlers.set(name, fn);
}

/** Look up a registered handler by name. Returns undefined if not registered. */
export function getActionHandler(name: string): ActionHandler | undefined {
  return handlers.get(name);
}

/** Remove a registered handler (mainly for testing). */
export function unregisterActionHandler(name: string): void {
  handlers.delete(name);
}

// ---------------------------------------------------------------------------
// Built-in handlers (platform-reserved __name__ prefix)
// ---------------------------------------------------------------------------
// Convention: `__name__` (double-underscore) names are reserved for engine built-ins.
// Manifest authors must not use `__`-prefixed names for custom handlers.

// Mirrors useCopyToClipboard.copy — same fail-soft try/catch — but inlined here
// because action-registry handlers run outside the React tree and can't use hooks.
registerActionHandler('__copy__', async ({ values, field, t }) => {
  const value = String(values[field.key] ?? '');
  if (typeof navigator === 'undefined' || !navigator.clipboard) {
    toast.error(t('Failed to copy to clipboard'));
    return;
  }
  try {
    await navigator.clipboard.writeText(value);
    toast.success(t('Copied to clipboard'));
  } catch {
    toast.error(t('Failed to copy to clipboard'));
  }
});
